/**
 * Signaling is only used to introduce two peers. Once the data channel opens,
 * nothing else goes through it, which is what lets a PHP-and-MySQL host stand
 * in for a realtime server.
 *
 * Two implementations exist: BroadcastChannel for two tabs on one machine, and
 * an HTTP poller for the PHP endpoints. The game code cannot tell them apart.
 */

export type SignalKind = 'join' | 'offer' | 'answer' | 'candidate' | 'leave';

export interface SignalMessage {
  /** Peer id of the sender. */
  from: string;
  /** Peer id of the recipient, or null to address the whole room. */
  to: string | null;
  kind: SignalKind;
  /** SDP or ICE candidate, as a JSON-safe value. */
  payload?: unknown;
}

export type SignalHandler = (message: SignalMessage) => void;

export interface Signaling {
  readonly room: string;
  readonly peerId: string;
  /** Begin receiving. Resolves once the transport is ready to carry messages. */
  start(): Promise<void>;
  send(message: Omit<SignalMessage, 'from'>): Promise<void>;
  /** Subscribe; returns an unsubscribe function. */
  onMessage(handler: SignalHandler): () => void;
  close(): void;
}

/** Room codes are four letters: short enough to read out across a desk. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';

export function isRoomCode(value: string): boolean {
  return /^[A-Z]{4}$/.test(value) && [...value].every((ch) => CODE_ALPHABET.includes(ch));
}

/**
 * Random room code. Ambiguous letters (I and O) are left out of the alphabet so
 * a code read aloud cannot be misheard as a digit.
 */
export function makeRoomCode(random: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < 4; i++) {
    out += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)] ?? 'A';
  }
  return out;
}

export function makePeerId(random: () => number = Math.random): string {
  return Math.floor(random() * 0xffffffff)
    .toString(36)
    .padStart(7, '0');
}

/** Shared bookkeeping for the handler list. */
export class SignalHandlers {
  private readonly handlers = new Set<SignalHandler>();

  add(handler: SignalHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  emit(message: SignalMessage): void {
    for (const handler of [...this.handlers]) handler(message);
  }

  clear(): void {
    this.handlers.clear();
  }
}

/** True if `message` is addressed to `peerId` (or to the whole room). */
export function addressedTo(message: SignalMessage, peerId: string): boolean {
  return message.from !== peerId && (message.to === null || message.to === peerId);
}
